import { LightningElement, api, track } from "lwc";
import {
  getFlagAndCodeForCountry,
  getCountrySelectOptions,
  decomposePhoneNumber,
} from "../../../shared/countryUtils";
import { translate } from "tbme/localization";
import {
  validatePhoneNumber,
  convertPhoneNumberToE164,
  formatPhoneNumber,
} from "../../../shared/phoneNumberValidation";

export default class PhoneNumberInput extends LightningElement {
  // Public API properties
  @api label: string = "";
  @api placeholder: string = "";
  @api required: boolean = false;
  @api disabled: boolean = false;
  @api errorMessage: string = "";
  @api supportingText: string = "";
  @api ariaLabel: string = "";
  @api name: string = "";
  @api id: string = "";

  private _value: string = "";
  private _generatedId: string = "";
  private _debounceTimer: ReturnType<typeof setTimeout> | undefined;

  @api
  get value(): string {
    return this._value;
  }

  set value(newValue: string) {
    if (newValue !== this._value) {
      this._value = newValue || "";
      this.initializeFromValue();
    }
  }

  @track selectedCountry: string = "US";
  @track displayValue: string = "";
  @track isFocused: boolean = false;
  @track internalErrorMessage: string = "";

  // Cached country options for performance
  private _countryOptions: Array<{
    label: string;
    value: string;
  }> | null = null;
  private _flagAndCode: { flag: string; code: string } | null = null;
  private _lastCountry: string = "";

  get countryOptions(): Array<{ label: string; value: string }> {
    if (!this._countryOptions) {
      this._countryOptions = getCountrySelectOptions();
    }
    return this._countryOptions;
  }

  get flagAndCode() {
    if (this._lastCountry !== this.selectedCountry || !this._flagAndCode) {
      this._flagAndCode = getFlagAndCodeForCountry(this.selectedCountry);
      this._lastCountry = this.selectedCountry;
    }
    return this._flagAndCode;
  }

  connectedCallback() {
    this.initializeFromValue();
    if (!this.id) {
      this._generatedId = `phone-input-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    }
  }

  disconnectedCallback() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }

  private initializeFromValue() {
    if (this._value) {
      const { countryName, phoneNumber } = decomposePhoneNumber(this._value);
      this.selectedCountry = countryName;
      this.displayValue = formatPhoneNumber(phoneNumber, this.selectedCountry);

      this.validateInput();
    } else {
      this.displayValue = "";
      this.internalErrorMessage = "";
    }
  }

  handleCountryChange(event: CustomEvent) {
    this.selectedCountry = event.detail;

    // Reformat existing number for new country
    if (this.displayValue) {
      const cleanedNumber = this.displayValue.replace(/[^\d]/g, "");
      this.displayValue = formatPhoneNumber(
        cleanedNumber,
        this.selectedCountry
      );
      this.updateInputField();
    }

    this.emitChangeEvent();
  }

  handlePhoneNumberInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;

    // Handle autofill (contains country code)
    if (inputValue.includes("+")) {
      const { countryName, phoneNumber } = decomposePhoneNumber(inputValue);
      this.selectedCountry = countryName;
      this.displayValue = formatPhoneNumber(phoneNumber, countryName);
    } else {
      // Regular input - format as you type
      this.displayValue = formatPhoneNumber(inputValue, this.selectedCountry);
    }

    this.updateInputField();
    this.emitChangeEvent();

    // Debounced validation
    this.debounceValidation();
  }

  private debounceValidation() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
    this._debounceTimer = setTimeout(() => this.validateInput(), 300);
  }

  private validateInput() {
    if (!this.displayValue) {
      this.internalErrorMessage = "";
      return;
    }

    const fullNumber = this.getFullPhoneNumber();
    const validation = validatePhoneNumber(fullNumber);
    this.internalErrorMessage = validation.isValid
      ? ""
      : validation.error || "";
  }

  private getFullPhoneNumber(): string {
    if (!this.displayValue) return "";
    const cleanedNumber = this.displayValue.replace(/[^\d]/g, "");
    return `+${this.flagAndCode.code}${cleanedNumber}`;
  }

  private updateInputField() {
    const input = this.template.querySelector(
      ".phone-input"
    ) as HTMLInputElement;
    if (input && input.value !== this.displayValue) {
      input.value = this.displayValue;
    }
  }

  private emitChangeEvent() {
    const fullNumber = this.getFullPhoneNumber();
    this._value = fullNumber;

    this.dispatchEvent(
      new CustomEvent("change", {
        detail: {
          value: fullNumber,
          e164: convertPhoneNumberToE164(fullNumber),
          formatted: this.displayValue,
          isValid: this.validate(),
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  handlePhoneNumberFocus() {
    this.isFocused = true;
  }
  handlePhoneNumberBlur() {
    this.isFocused = false;
    this.validateInput();
  }
  handleCountrySelectFocus() {
    this.isFocused = true;
  }
  handleCountrySelectBlur() {
    this.isFocused = false;
  }

  get phoneInputClasses() {
    const classes = ["phone-input-container"];
    if (this.hasError) classes.push("error");
    if (this.disabled) classes.push("disabled");
    if (this.isFocused) classes.push("focused");
    return classes.join(" ");
  }

  get hasError() {
    return !!(this.errorMessage || this.internalErrorMessage);
  }

  get displayErrorMessage() {
    return this.errorMessage || this.internalErrorMessage;
  }

  get showSupportingText() {
    return !this.hasError && this.supportingText;
  }

  get isRequiredField() {
    return this.required && this.label;
  }

  get effectiveId() {
    return this.id || this._generatedId;
  }

  get phoneInputAriaLabel() {
    return this.ariaLabel || this.label || "Phone number";
  }

  get countrySelectAriaLabel() {
    return (
      translate("profileEditModal.fieldCountryCode") || "Select country code"
    );
  }

  get inputDisabled() {
    return this.disabled;
  }

  get countrySelectDisabled() {
    return this.disabled;
  }

  // Public API methods
  @api
  validate(): boolean {
    if (this.required && !this._value) {
      this.internalErrorMessage = "Phone number is required";
      return false;
    }
    if (!this._value) {
      this.internalErrorMessage = "";
      return true;
    }

    const validation = validatePhoneNumber(this._value);
    this.internalErrorMessage = validation.isValid
      ? ""
      : validation.error || "Enter a valid phone number";
    return validation.isValid;
  }

  @api
  getFormattedValue(): string {
    return this.displayValue;
  }

  @api
  getE164Format(): string {
    return convertPhoneNumberToE164(this._value);
  }

  @api
  setValue(newValue: string) {
    this.value = newValue;
    this.emitChangeEvent();
  }

  @api
  clear() {
    this._value = "";
    this.displayValue = "";
    this.internalErrorMessage = "";
    this.updateInputField();
    this.emitChangeEvent();
  }

  @api
  focus() {
    const input = this.template.querySelector(
      ".phone-input"
    ) as HTMLInputElement;
    input?.focus();
  }

  @api
  blur() {
    const input = this.template.querySelector(
      ".phone-input"
    ) as HTMLInputElement;
    input?.blur();
  }
}
